import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { RADIO_INITIAL_STATE, RadioState } from './radio.model';
import { withCallState } from '@shared/store-features/call-state-feature';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import {
  createRadio,
  deleteRadio,
  deleteRadiosMany,
  editRadioRow
} from '@shared/api/entertainment-api/radio-api';
import axios from 'axios';
import {
  createGenre,
  deleteGenre,
  editGenre,
  getGenres
} from '@api/entertainment-api/entertainment-api.ts';
import { GenresEnum } from '@modules/entertainment/models/entertainment.models.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useRadioStore = create<RadioState>()(
  devtools(
    (set, getState) => ({
      ...RADIO_INITIAL_STATE,
      ...withCallState(getState),

      getGenreList: async () => {
        try {
          const genreData = await getGenres(GenresEnum.Radio);

          const genreList = genreData.map((genre) => ({
            id: genre._id,
            name: genre?.name?.['en'],
            title: genre?.name?.['en'],
            image: genre.image,
            value: genre?.name?.['en']
          }));

          set({
            genreList
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createRow: async ({ data, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await createRadio(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_radio_create_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      editRow: async ({ data, state, handleClose, id }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editRadioRow(data, id);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_update_radio_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteRow: async ({ id, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          if (id instanceof Array) {
            await deleteRadiosMany(id);
          } else {
            await deleteRadio(id);
          }
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar('snackbar_delete_radio_text', SnackbarType.Success);
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      createGenre: async ({ data, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await createGenre(data);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }

          throw error;
        }
      },

      editGenre: async ({ data, id, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await editGenre(data as any, id);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      deleteGenre: async ({ id, handleClose }) => {
        set({ genreCallState: CallStateEnum.Loading });
        try {
          await deleteGenre(id);
          await getState().getGenreList();
          set({ genreCallState: CallStateEnum.Loaded });
          handleClose?.();
        } catch (error) {
          set({ genreCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      isGenreLoading: () => getState().genreCallState === CallStateEnum.Loading,
      isGenreLoaded: () => getState().genreCallState === CallStateEnum.Loaded,
      isGenreError: () => getState().genreCallState === CallStateEnum.Error
    }),

    { name: 'RadioStore' }
  )
);

export default useRadioStore;
